import { Box, Card, Chip, Grid, Tab, Tabs, Typography, useTheme, Avatar, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useState, useEffect } from "react";
import { DateRange, ButtonBasic } from "../../components";
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import PointOfSaleOutlinedIcon from '@mui/icons-material/PointOfSaleOutlined';
import QuestionAnswerOutlinedIcon from '@mui/icons-material/QuestionAnswerOutlined';
import CommentOutlinedIcon from '@mui/icons-material/CommentOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AnaliticaAsesorService from "../../services/analiticaAsesor.service";
import FechaFunction from "../../function/FechaFunction";

function TabPanel({ children, value, index }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            style={{ maxHeight: '65vh', overflow: 'auto' }}
            className='side'
        >
            {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
        </div>
    );
}

/* ----- Card KPI General ----- */
const KpiCard = ({ title, value, icon, color, subtitle, isDarkMode }) => (
    <Card sx={{
        p: 2.5,
        borderRadius: 3,
        background: isDarkMode ? '#2d3a52' : '#fff',
        boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
        height: '100%',
    }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box>
                <Typography variant="body2" sx={{ color: isDarkMode ? '#90a4ae' : '#666', fontSize: 12, mb: 0.5 }}>
                    {title}
                </Typography>
                <Typography variant="h4" sx={{ color: isDarkMode ? '#e0e0e0' : '#1a1a1a', fontWeight: 'bold' }}>
                    {value}
                </Typography>
            </Box>
            <Box sx={{
                width: 48, height: 48, borderRadius: 2,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: `${color}20`
            }}>
                {icon}
            </Box>
        </Box>
        {subtitle && (
            <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999', mt: 1 }}>
                {subtitle}
            </Typography>
        )}
    </Card>
);

/* ----- Card Ranking de Asesor ----- */
const RankingAsesorCard = ({ rank, asesor, metricas, isDarkMode }) => {
    const medalColors = { 1: '#FFD700', 2: '#C0C0C0', 3: '#CD7F32' };
    const medalColor = medalColors[rank] || '#64B5F6';
    
    return (
        <Card sx={{
            p: 2,
            borderRadius: 2,
            background: isDarkMode ? '#2d3a52' : '#fff',
            border: rank <= 3 ? `2px solid ${medalColor}` : `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
        }}>
            <Box display="flex" alignItems="center" gap={2}>
                <Box position="relative">
                    <Avatar
                        src={asesor.foto_p ? `/uploads/fotos/${asesor.foto_p}` : undefined}
                        sx={{ width: 56, height: 56, bgcolor: medalColor }}
                    >
                        {asesor.nombre_p?.[0]}
                    </Avatar>
                    {rank <= 3 && (
                        <Box sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            background: medalColor,
                            borderRadius: '50%',
                            width: 24,
                            height: 24,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <EmojiEventsIcon sx={{ fontSize: 14, color: '#fff' }} />
                        </Box>
                    )}
                </Box>
                <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="h6" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold' }}>
                            #{rank}
                        </Typography>
                        <Typography variant="subtitle1" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 600 }}>
                            {asesor.nombre_p}
                        </Typography>
                    </Box>
                    <Grid container spacing={1} mt={0.5}>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>Tickets</Typography>
                            <Typography variant="body2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 600 }}>
                                {metricas.tickets}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>Cerrados</Typography>
                            <Typography variant="body2" sx={{ color: isDarkMode ? '#4caf50' : '#2e7d32', fontWeight: 600 }}>
                                {metricas.ticketsCerrados}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>Ventas</Typography>
                            <Typography variant="body2" sx={{ color: isDarkMode ? '#9c27b0' : '#7b1fa2', fontWeight: 600 }}>
                                {metricas.ventas}
                            </Typography>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>Consultas</Typography>
                            <Typography variant="body2" sx={{ color: isDarkMode ? '#00bcd4' : '#0097a7', fontWeight: 600 }}>
                                {metricas.consultas}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Card>
    );
};

/* ----- Distribución Horizontal ----- */
const DistribucionCard = ({ title, data, isDarkMode, colors }) => {
    const total = Object.values(data).reduce((a, b) => a + b, 0);
    if (total === 0) return null;
    const colorsArr = colors || ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'];
    const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]).slice(0, 10);
    
    return (
        <Card sx={{
            p: 2.5, borderRadius: 3,
            background: isDarkMode ? '#2d3a52' : '#fff',
            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
        }}>
            <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold', mb: 2 }}>
                {title}
            </Typography>
            {sorted.map(([label, val], idx) => {
                const pct = ((val / total) * 100).toFixed(1);
                return (
                    <Box key={label} sx={{ mb: 1.5 }}>
                        <Box display="flex" justifyContent="space-between" mb={0.3}>
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#b0bec5' : '#555' }}>
                                {label}
                            </Typography>
                            <Typography variant="caption" sx={{ color: isDarkMode ? '#90a4ae' : '#777', fontWeight: 500 }}>
                                {val} ({pct}%)
                            </Typography>
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={parseFloat(pct)}
                            sx={{
                                height: 8, borderRadius: 4,
                                backgroundColor: isDarkMode ? '#1a2332' : '#f0f0f0',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    backgroundColor: colorsArr[idx % colorsArr.length],
                                }
                            }}
                        />
                    </Box>
                );
            })}
        </Card>
    );
};

/* ----- Timeline ----- */
const TimelineCard = ({ title, data, isDarkMode, color }) => {
    const entries = Object.entries(data).sort((a, b) => a[0].localeCompare(b[0]));
    if (entries.length === 0) return null;
    const maxVal = Math.max(...entries.map(e => e[1]), 1);
    const total = entries.reduce((acc, [, v]) => acc + v, 0);
    const BAR_MAX_HEIGHT = 100;
    
    return (
        <Card sx={{
            p: 2.5, borderRadius: 3,
            background: isDarkMode ? '#2d3a52' : '#fff',
            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
            minHeight: 250
        }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold' }}>
                    {title}
                </Typography>
                <Typography variant="caption" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>
                    Total: {total}
                </Typography>
            </Box>
            <Box sx={{ overflowX: 'auto', overflowY: 'hidden', pb: 1 }} className="side">
                <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'flex-end',
                    gap: '3px',
                    minWidth: '100%',
                    height: BAR_MAX_HEIGHT + 70,
                    pt: 2,
                }}>
                    {entries.map(([dia, val]) => {
                        const barH = Math.max(Math.round((val / maxVal) * BAR_MAX_HEIGHT), 4);
                        return (
                            <Box key={dia} sx={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                minWidth: entries.length > 60 ? 18 : 28,
                                flexShrink: 0,
                            }}>
                                <Typography variant="caption" sx={{
                                    color: isDarkMode ? '#90a4ae' : '#888',
                                    fontSize: entries.length > 60 ? 8 : 10,
                                    fontWeight: val === maxVal ? 700 : 400,
                                }}>
                                    {val}
                                </Typography>
                                <Box sx={{
                                    width: entries.length > 60 ? 12 : 18,
                                    height: barH,
                                    borderRadius: '3px 3px 0 0',
                                    background: val === maxVal ? color : `${color}99`,
                                    '&:hover': { background: color, transform: 'scaleX(1.3)' }
                                }} />
                                <Typography variant="caption" sx={{
                                    color: isDarkMode ? '#78909c' : '#aaa',
                                    fontSize: entries.length > 60 ? 7 : 9,
                                    writingMode: 'vertical-rl',
                                    maxHeight: 56,
                                }}>
                                    {dia.split('-').reverse().join('/')}
                                </Typography>
                            </Box>
                        );
                    })}
                </Box>
            </Box>
        </Card>
    );
};

/* ----- Tabla Detalle de Ventas ----- */
const DetalleVentasTable = ({ data, isDarkMode }) => {
    if (!data || data.length === 0) return null;

    const estadoColor = (estado) => {
        if (!estado) return isDarkMode ? '#b0bec5' : '#555';
        const e = estado.toUpperCase();
        if (['ACTIVO', 'SERVICIO HABILITADO'].includes(e)) return '#4caf50';
        if (e === 'BAJA') return '#f44336';
        if (e === 'CANCELADO') return '#ff9800';
        return isDarkMode ? '#b0bec5' : '#555';
    };

    return (
        <Card sx={{
            borderRadius: 3,
            background: isDarkMode ? '#2d3a52' : '#fff',
            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
        }}>
            <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold' }}>
                    Detalle de Ventas ({data.length})
                </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            {['ID', 'Asesor', 'Cliente', 'Plan', 'Estado', 'Tecnología', 'Categoría', 'Dirección', 'Fecha'].map((h) => (
                                <TableCell key={h} sx={{
                                    background: isDarkMode ? '#2d3a52' : '#fafafa',
                                    color: isDarkMode ? '#90a4ae' : '#666',
                                    fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap',
                                }}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, idx) => (
                            <TableRow key={idx} sx={{ '&:hover': { background: isDarkMode ? '#253044' : '#f5f5f5' } }}>
                                <TableCell sx={{ color: isDarkMode ? '#64b5f6' : '#1976d2', fontWeight: 600, fontSize: 12 }}>
                                    #{row.id_t}
                                </TableCell>
                                <TableCell sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontSize: 12, whiteSpace: 'nowrap' }}>
                                    {row.asesor}
                                </TableCell>
                                <TableCell sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontSize: 12, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.nombre_t}
                                </TableCell>
                                <TableCell sx={{ color: isDarkMode ? '#b0bec5' : '#555', fontSize: 12, whiteSpace: 'nowrap' }}>
                                    {row.plan || '-'}
                                </TableCell>
                                <TableCell sx={{ fontSize: 12 }}>
                                    <Chip
                                        label={row.estadoServicio_t || 'Sin Estado'}
                                        size="small"
                                        sx={{
                                            height: 22, fontSize: 10, fontWeight: 600,
                                            color: '#fff',
                                            backgroundColor: estadoColor(row.estadoServicio_t),
                                        }}
                                    />
                                </TableCell>
                                <TableCell sx={{ color: isDarkMode ? '#b0bec5' : '#555', fontSize: 12 }}>
                                    {row.tipoTecnologia_t || '-'}
                                </TableCell>
                                <TableCell sx={{ color: isDarkMode ? '#b0bec5' : '#555', fontSize: 12 }}>
                                    {row.categoria_t || '-'}
                                </TableCell>
                                <TableCell sx={{ color: isDarkMode ? '#b0bec5' : '#555', fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {row.direccion_t || '-'}
                                </TableCell>
                                <TableCell sx={{ color: isDarkMode ? '#78909c' : '#999', fontSize: 12, whiteSpace: 'nowrap' }}>
                                    {row.createdAt}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
};

/* ----- Tabla Comparativa de Asesores ----- */
const TablaComparativaAsesores = ({ data, isDarkMode }) => {
    if (!data || data.length === 0) return null;
    
    return (
        <Card sx={{
            borderRadius: 3,
            background: isDarkMode ? '#2d3a52' : '#fff',
            boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
            border: `1px solid ${isDarkMode ? '#3d4a62' : '#e0e0e0'}`,
        }}>
            <Box sx={{ p: 2 }}>
                <Typography variant="subtitle2" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold', mb: 2 }}>
                    Tabla Comparativa General
                </Typography>
            </Box>
            <TableContainer sx={{ maxHeight: 500 }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ background: isDarkMode ? '#2d3a52' : '#fafafa', color: isDarkMode ? '#90a4ae' : '#666', fontWeight: 600 }}>
                                Asesor
                            </TableCell>
                            <TableCell align="right" sx={{ background: isDarkMode ? '#2d3a52' : '#fafafa', color: isDarkMode ? '#90a4ae' : '#666', fontWeight: 600 }}>
                                Tickets
                            </TableCell>
                            <TableCell align="right" sx={{ background: isDarkMode ? '#2d3a52' : '#fafafa', color: isDarkMode ? '#90a4ae' : '#666', fontWeight: 600 }}>
                                Cerrados
                            </TableCell>
                            <TableCell align="right" sx={{ background: isDarkMode ? '#2d3a52' : '#fafafa', color: isDarkMode ? '#90a4ae' : '#666', fontWeight: 600 }}>
                                Vencidos
                            </TableCell>
                            <TableCell align="right" sx={{ background: isDarkMode ? '#2d3a52' : '#fafafa', color: isDarkMode ? '#90a4ae' : '#666', fontWeight: 600 }}>
                                Ventas
                            </TableCell>
                            <TableCell align="right" sx={{ background: isDarkMode ? '#2d3a52' : '#fafafa', color: isDarkMode ? '#90a4ae' : '#666', fontWeight: 600 }}>
                                Consultas
                            </TableCell>
                            <TableCell align="right" sx={{ background: isDarkMode ? '#2d3a52' : '#fafafa', color: isDarkMode ? '#90a4ae' : '#666', fontWeight: 600 }}>
                                Comentarios
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data.map((row, idx) => (
                            <TableRow key={idx} sx={{ '&:hover': { background: isDarkMode ? '#253044' : '#f5f5f5' } }}>
                                <TableCell sx={{ color: isDarkMode ? '#e0e0e0' : '#333' }}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Avatar src={row.asesor.foto_p} sx={{ width: 28, height: 28 }}>
                                            {row.asesor.nombre_p?.[0]}
                                        </Avatar>
                                        {row.asesor.nombre_p}
                                    </Box>
                                </TableCell>
                                <TableCell align="right" sx={{ color: isDarkMode ? '#b0bec5' : '#555', fontWeight: 600 }}>
                                    {row.tickets.total}
                                </TableCell>
                                <TableCell align="right" sx={{ color: isDarkMode ? '#4caf50' : '#2e7d32', fontWeight: 600 }}>
                                    {row.tickets.cerrados}
                                </TableCell>
                                <TableCell align="right" sx={{ color: isDarkMode ? '#ff9800' : '#f57c00', fontWeight: 600 }}>
                                    {row.tickets.vencidos}
                                </TableCell>
                                <TableCell align="right" sx={{ color: isDarkMode ? '#9c27b0' : '#7b1fa2', fontWeight: 600 }}>
                                    {row.ventas.total}
                                </TableCell>
                                <TableCell align="right" sx={{ color: isDarkMode ? '#00bcd4' : '#0097a7', fontWeight: 600 }}>
                                    {row.consultas.total}
                                </TableCell>
                                <TableCell align="right" sx={{ color: isDarkMode ? '#607d8b' : '#455a64', fontWeight: 600 }}>
                                    {row.comentarios.total}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Card>
    );
};

/* ============================================ */
/* PANTALLA PRINCIPAL                           */
/* ============================================ */
const AnaliticaAsesorGeneralScreen = () => {
    const theme = useTheme();
    const isDarkMode = theme.palette.mode === 'dark';
    const [tab, setTab] = useState(0);
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [fechaSelect, setFechaSelect] = useState({
        startDate: new Date(new Date().setDate(new Date().getDate() - 7)),
        endDate: new Date()
    });

    const buscarAnalitica = () => {
        setLoading(true);
        const body = {
            fecIni: FechaFunction.format(fechaSelect.startDate, 'yyyy-MM-dd'),
            fecFin: FechaFunction.format(fechaSelect.endDate, 'yyyy-MM-dd'),
        };
        AnaliticaAsesorService.getAnaliticaGeneral(body).then((res) => {
            setData(res?.data || res);
        }).catch((err) => {
            console.error(err);
            setData(null);
        }).finally(() => setLoading(false));
    };

    useEffect(() => {
        document.title = 'Analítica General - Asesores';
    }, []);

    // Top 5 asesores por actividad total en el período seleccionado
    const topAsesores = data?.porAsesor
        ? [...data.porAsesor]
            .map(item => ({
                ...item,
                totalActividad: (item.tickets?.total || 0) + (item.ventas?.total || 0) + (item.consultas?.total || 0) + (item.comentarios?.total || 0)
            }))
            .filter(item => item.totalActividad > 0)
            .sort((a, b) => b.totalActividad - a.totalActividad)
            .slice(0, 5)
        : [];

    return (
        <Box sx={{ width: '100%', height: '100%', overflow: 'auto', background: isDarkMode ? '#1a2332' : '#f5f5f5' }} className='side'>
            <Grid container p={3} spacing={2}>
                {/* Header */}
                <Grid item xs={12}>
                    <Typography sx={{ color: isDarkMode ? '#e0e0e0' : '#000', fontWeight: 'bold' }} variant="h4">
                        Analítica General de Asesores
                    </Typography>
                    <Typography variant="body2" sx={{ color: isDarkMode ? '#78909c' : '#666', mt: 0.5 }}>
                        Vista consolidada de métricas de todos los asesores
                    </Typography>
                </Grid>

                {/* Filtros */}
                <Grid item xs={12}>
                    <Card sx={{ p: 2.5, borderRadius: 3, background: isDarkMode ? '#2d3a52' : '#fff', boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)' }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} md={10}>
                                <Typography variant="caption" sx={{ color: isDarkMode ? '#90a4ae' : '#666', mb: 0.5, display: 'block' }}>
                                    Rango de Fechas
                                </Typography>
                                <DateRange
                                    minDate={new Date('2024-01-01')}
                                    initianDate={fechaSelect}
                                    onChange={setFechaSelect}
                                    maxDays={180}
                                />
                                <Typography variant="caption" sx={{ 
                                    color: isDarkMode ? '#78909c' : '#999', 
                                    mt: 0.5,
                                    display: 'block',
                                    fontSize: 11
                                }}>
                                    Máximo 180 días
                                </Typography>
                            </Grid>
                            <Grid item xs={12} md={2} sx={{ display: 'flex', alignItems: 'flex-end' }}>
                                <ButtonBasic
                                    variant='wk'
                                    color='nextStep'
                                    textButton="Buscar"
                                    iconStart={<SearchIcon />}
                                    onClick={buscarAnalitica}
                                    isLoading={loading}
                                    sx={{ height: 42, width: '100%' }}
                                />
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>

                {/* KPIs Generales */}
                {data?.totales && (
                    <>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Total Tickets"
                                value={data.totales.totalTicketsCreados}
                                icon={<ConfirmationNumberOutlinedIcon sx={{ color: '#2196f3' }} />}
                                color="#2196f3"
                                subtitle={`${data.totales.ticketsAbiertos} abiertos · ${data.totales.ticketsCerrados} cerrados`}
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Cerrados"
                                value={data.totales.ticketsCerradosPorAsesores}
                                icon={<CheckCircleOutlineIcon sx={{ color: '#4caf50' }} />}
                                color="#4caf50"
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Vencidos"
                                value={data.totales.ticketsVencidos}
                                icon={<WarningAmberIcon sx={{ color: '#ff9800' }} />}
                                color="#ff9800"
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Total Ventas"
                                value={data.totales.totalVentas}
                                icon={<PointOfSaleOutlinedIcon sx={{ color: '#9c27b0' }} />}
                                color="#9c27b0"
                                subtitle={`${data.totales.ventasActivas} activas`}
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Consultas"
                                value={data.totales.totalConsultas}
                                icon={<QuestionAnswerOutlinedIcon sx={{ color: '#00bcd4' }} />}
                                color="#00bcd4"
                                isDarkMode={isDarkMode}
                            />
                        </Grid>
                        <Grid item xs={6} md={3} lg={2}>
                            <KpiCard
                                title="Comentarios"
                                value={data.totales.totalComentarios}
                                icon={<CommentOutlinedIcon sx={{ color: '#607d8b' }} />}
                                color="#607d8b"
                                isDarkMode={isDarkMode}
                            />
                        </Grid>

                        {/* Top 5 Asesores */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{ color: isDarkMode ? '#e0e0e0' : '#333', fontWeight: 'bold', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <TrendingUpIcon sx={{ color: '#4caf50' }} />
                                Top 5 Asesores
                            </Typography>
                            <Grid container spacing={2}>
                                {topAsesores.map((item, idx) => (
                                    <Grid item xs={12} md={6} lg={4} key={idx}>
                                        <RankingAsesorCard
                                            rank={idx + 1}
                                            asesor={item.asesor}
                                            metricas={{
                                                tickets: item.tickets.total,
                                                ticketsCerrados: item.tickets.cerrados,
                                                ventas: item.ventas.total,
                                                consultas: item.consultas.total
                                            }}
                                            isDarkMode={isDarkMode}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </Grid>

                        {/* Tabs */}
                        <Grid item xs={12}>
                            <Card sx={{
                                background: isDarkMode ? '#2d3a52' : '#fff',
                                borderRadius: 3,
                                boxShadow: isDarkMode ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(0,0,0,0.08)',
                            }}>
                                <Box sx={{ borderBottom: 1, borderColor: isDarkMode ? '#3d4a62' : '#e0e0e0' }}>
                                    <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable"
                                        sx={{
                                            '& .MuiTab-root': { color: isDarkMode ? '#90a4ae' : '#666', textTransform: 'none' },
                                            '& .Mui-selected': { color: isDarkMode ? '#64b5f6 !important' : '#1976d2 !important' },
                                            '& .MuiTabs-indicator': { backgroundColor: isDarkMode ? '#64b5f6' : '#1976d2' },
                                        }}
                                    >
                                        <Tab label="Vista General" />
                                        <Tab label="Tickets" />
                                        <Tab label="Ventas" />
                                        <Tab label="Consultas" />
                                    </Tabs>
                                </Box>

                                {/* TAB GENERAL */}
                                <TabPanel value={tab} index={0}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TablaComparativaAsesores data={data.porAsesor} isDarkMode={isDarkMode} />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                {/* TAB TICKETS */}
                                <TabPanel value={tab} index={1}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Asesor" data={data.tickets?.porAsesor || {}} isDarkMode={isDarkMode} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Stage" data={data.tickets?.porStage || {}} isDarkMode={isDarkMode} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Tipo" data={data.tickets?.porTipo || {}} isDarkMode={isDarkMode}
                                                colors={['#4caf50', '#ff9800', '#f44336', '#2196f3']}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Motivo" data={data.tickets?.porMotivo || {}} isDarkMode={isDarkMode}
                                                colors={['#e91e63', '#3f51b5', '#009688', '#ff5722']}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TimelineCard title="Tickets por Día" data={data.tickets?.porDia || {}} isDarkMode={isDarkMode} color="#2196f3" />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                {/* TAB VENTAS */}
                                <TabPanel value={tab} index={2}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Asesor" data={data.ventas?.porAsesor || {}} isDarkMode={isDarkMode} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Estado" data={data.ventas?.porEstado || {}} isDarkMode={isDarkMode}
                                                colors={['#4caf50', '#ff9800', '#f44336']}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Tecnología" data={data.ventas?.porTecnologia || {}} isDarkMode={isDarkMode}
                                                colors={['#2196f3', '#4caf50', '#ff9800']}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Planes" data={data.ventas?.porPlanes || {}} isDarkMode={isDarkMode} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TimelineCard title="Ventas por Día" data={data.ventas?.porDia || {}} isDarkMode={isDarkMode} color="#9c27b0" />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <DetalleVentasTable data={data.ventas?.detalle || []} isDarkMode={isDarkMode} />
                                        </Grid>
                                    </Grid>
                                </TabPanel>

                                {/* TAB CONSULTAS */}
                                <TabPanel value={tab} index={3}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Asesor" data={data.consultas?.porAsesor || {}} isDarkMode={isDarkMode} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <DistribucionCard title="Por Estado" data={data.consultas?.porEstado || {}} isDarkMode={isDarkMode}
                                                colors={['#00bcd4', '#4caf50', '#ff9800', '#f44336']}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TimelineCard title="Consultas por Día" data={data.consultas?.porDia || {}} isDarkMode={isDarkMode} color="#00bcd4" />
                                        </Grid>
                                    </Grid>
                                </TabPanel>
                            </Card>
                        </Grid>
                    </>
                )}
                {!data && !loading ? (
                    <Grid item xs={12}>
                        <Card sx={{ p: 6, borderRadius: 3, background: isDarkMode ? '#2d3a52' : '#fff', textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: isDarkMode ? '#78909c' : '#999' }}>
                                Seleccione un rango de fechas y presione "Buscar"
                            </Typography>
                        </Card>
                    </Grid>
                ):(
                    <Grid item xs={12}>
                        <Box sx={{width: 100, height: 30}} />
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default AnaliticaAsesorGeneralScreen;
